import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./CourriersDuCoeur.scss";
import "../styles/clouds.scss";
import RenardLoading from "../assets/lottie/renard_chargement";
import Lottie from "lottie-react";
import VideoSpectacle from "./PagesComponents/AvantSpectacle/VideoSpectacle";
import Nav from "../components/Layout/Nav";

import clouds from "../assets/img/intro/clouds.webp";
import coeur from "../assets/img/courriers/coeur.png";
import iconCourrierGrou from "../assets/img/courriers/courrier_grou_picto.webp";
import iconCourrierFoxes from "../assets/img/courriers/courrier_foxes_picto2.webp";
import iconCourrierTadam from "../assets/img/courriers/courrier_tadam_picto.webp";

// ============================================================================
// CONSTANTS
// ============================================================================
const API_ENDPOINT = "https://cierenards.be/wp-json/renards/v1/build/courrier";
const FOLLOWER_CLASSES = ["first", "second", "third", "fourth"];
const COURRIER_TYPES = {
	GROU: "grou",
	FOXES: "foxes",
	TADAM: "tadam",
};

const COURRIER_BUTTONS = [
	{ type: COURRIER_TYPES.GROU, label: "Grou", icon: iconCourrierGrou },
	{ type: COURRIER_TYPES.FOXES, label: "Foxes", icon: iconCourrierFoxes },
	{ type: COURRIER_TYPES.TADAM, label: "Tadam", icon: iconCourrierTadam },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const shuffleArray = (array) => {
	const shuffled = [...array];
	let currentIndex = shuffled.length;

	while (currentIndex !== 0) {
		const randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex--;
		[shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]];
	}

	return shuffled;
};

const prepareDrawingsFromData = (data) => {
	if (!data || !Array.isArray(data)) return [];

	const urls = data
		.filter((drawing) => drawing?.sizes?.medium_large)
		.map((drawing) => drawing.sizes.medium_large);

	return shuffleArray(urls);
};

const isTouchEnabled = () => {
	return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function CourriersDuCoeur() {
	// State
	const [currentCourrier, setCurrentCourrier] = useState(COURRIER_TYPES.GROU);
	const [gallerieData, setGallerieData] = useState([]);
	const [currentPicIndex, setCurrentPicIndex] = useState(0);
	const [displayedPictures, setDisplayedPictures] = useState([]);
	const [isIndicationHidden, setIsIndicationHidden] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [courriersData, setCourriersData] = useState(null);

	// Refs
	const followerRef = useRef(null);

	// ============================================================================
	// EFFECTS
	// ============================================================================

	// Fetch courriers data on mount
	useEffect(() => {
		const fetchCourriers = async () => {
			try {
				setLoading(true);
				const response = await fetch(API_ENDPOINT);

				if (!response.ok) {
					throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
				}

				// Vérifier que la réponse est bien du JSON
				const contentType = response.headers.get("content-type");
				if (!contentType || !contentType.includes("application/json")) {
					throw new Error("La réponse de l'API n'est pas du JSON valide");
				}

				const data = await response.json();
				setCourriersData(data);
				console.log(data);
			} catch (err) {
				console.error("Erreur lors du chargement des courriers:", err);
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchCourriers();
	}, []);

	// Update gallery when courriers data or type changes
	useEffect(() => {
		if (!courriersData) return;

		const dataKey = `courriers_${currentCourrier}`;
		const drawings = courriersData[dataKey];

		if (drawings) {
			const preparedData = prepareDrawingsFromData(drawings);
			setGallerieData(preparedData);
		}
	}, [courriersData, currentCourrier]);

	// Mouse move listener with cleanup
	useEffect(() => {
		const handleMouseMove = (ev) => {
			if (!followerRef.current) return;
			followerRef.current.style.left = `${ev.pageX}px`;
			followerRef.current.style.top = `${ev.pageY}px`;
		};

		window.addEventListener("mousemove", handleMouseMove);

		// Cleanup to prevent memory leak
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
		};
	}, []);

	// ============================================================================
	// HANDLERS
	// ============================================================================

	const handleClick = useCallback(
		(ev) => {
			if (!ev || gallerieData.length === 0) return;

			// Hide indication on first click
			if (!isIndicationHidden) {
				setIsIndicationHidden(true);
			}

			// Add current picture to displayed pictures
			setDisplayedPictures((prev) => [
				...prev,
				{
					mediaItemUrl: gallerieData[currentPicIndex],
					x: ev.pageX,
					y: ev.pageY,
				},
			]);

			// Move to next picture
			setCurrentPicIndex((prevIndex) => (prevIndex < gallerieData.length - 1 ? prevIndex + 1 : 0));
		},
		[gallerieData, currentPicIndex, isIndicationHidden]
	);

	const switchCourrierType = useCallback((type) => {
		setCurrentCourrier(type);
		setCurrentPicIndex(0);
		setIsIndicationHidden(false);
		setDisplayedPictures([]);
	}, []);

	// ============================================================================
	// COMPUTED VALUES
	// ============================================================================

	const followerClassName = useMemo(() => {
		return FOLLOWER_CLASSES[currentPicIndex % FOLLOWER_CLASSES.length];
	}, [currentPicIndex]);

	const touchEnabled = useMemo(() => isTouchEnabled(), []);

	// ============================================================================
	// RENDER CONDITIONS
	// ============================================================================

	if (loading) {
		return (
			<div className='page_container sky page_courrier is_loading'>
				<Nav />
				<div className='loading_container'>
					<Lottie className='renardLoading' animationData={RenardLoading} loop autoplay />
					<p>Chargement du courrier…</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='page_container sky page_courrier'>
				<div className='loading_container'>Error! {error}</div>
			</div>
		);
	}

	// ============================================================================
	// MAIN RENDER
	// ============================================================================

	return (
		<main className='page_container sky page_courrier'>
			<Nav />

			<img className='clouds_1' src={clouds} alt='' width={704} height={462} />
			<img className='clouds_2' src={clouds} alt='' width={704} height={462} />

			<div className='titling'>
				<h1>
					Les courriers du <img className='coeur' src={coeur} alt='' />
				</h1>

				<div className='courrier_selection'>
					{COURRIER_BUTTONS.map(({ type, label, icon }) => (
						<button
							key={type}
							className={`button button--orange button--rounded button--centered button--oliver ${
								currentCourrier === type
									? "is_current"
									: "is_not_current"
							}`}
							disabled={currentCourrier === type}
							onClick={() => switchCourrierType(type)}>
							<img className='button_icon' src={icon} alt='' />
							{label}
						</button>
					))}
				</div>
			</div>

			<div className='courrier_content_container' onClick={handleClick}>
				<div ref={followerRef} className={`follower ${followerClassName}`}>
					{gallerieData[currentPicIndex] && (
						<img
							src={gallerieData[currentPicIndex]}
							alt="Un super dessin d'enfant"
						/>
					)}
				</div>

				<div className={`indicationZone ${isIndicationHidden ? "is_hidden" : ""}`}>
					<VideoSpectacle
						webmUrl='/videos/arrows/Arrow_Element_24_Color-vp9-chrome.webm'
						mp4Url='/videos/arrows/Arrow_Element_24_Color-hevc-safari.mp4'
						loop={true}
					/>
					<p>
						{touchEnabled
							? "Tape ici pour déposer des dessins et voir les suivants"
							: "Clique ici pour déposer les dessins et voir les suivants"}
					</p>
				</div>

				{displayedPictures.map((picture, index) => (
					<div
						className='displayedPicture'
						key={`${picture.x}-${picture.y}-${index}`}
						style={{
							left: `${picture.x}px`,
							top: `${picture.y}px`,
						}}>
						<img src={picture.mediaItemUrl} alt='' />
					</div>
				))}
			</div>

			<div className='lol'>
				<a
					className='cta_orangered submitDrawing'
					href='https://cierenards.be/proposes-ton-dessin/'>
					Proposer mon dessin
				</a>
			</div>
		</main>
	);
}
